import type { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import {
  faArrowRight,
  faBuilding,
  faCircle,
  faCube,
  faLayerGroup,
  faPlus,
  faTrash,
  faUserLock,
  faUsers,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Badge,
  Button,
  Grid,
  Icon,
  Inline,
  Message,
  Modal,
  Section,
  Tooltip,
} from "@intility/bifrost-react";
import { useState } from "react";
import { Link } from "react-router";
import {
  useCancelRequest,
  useClientAccess,
  useClientRequests,
  useRevokeAccess,
} from "../api/access";
import { useCompanies } from "../api/catalog";
import type { ClientListItem } from "../api/types";
import {
  type AccessGrant,
  type AccessTreeNode,
  buildAccessTree,
} from "../lib/accessTree";
import {
  AddPermissionDrawer,
  type Notice,
} from "../pages/clients/ClientDetail/ClientAccessSection";
import { STATUS } from "./ClientApiAccess";
import { RoleBadge } from "./RoleBadge";
import { useCanEditClient } from "./useCanEditClient";
import "./ClientApiAccess.css";

interface DataItem {
  key: string;
  title: string;
  subtitle: string;
  role?: string;
  status: string;
  onRevoke?: () => void;
  onCancel?: () => void;
}

// A target reads as its name; groups lose their "group:" prefix and say so below.
function describeTarget(target: string): { title: string; subtitle: string } {
  const [type, ...rest] = target.split(":");
  if (rest.length === 0) return { title: target, subtitle: "Object" };
  const label = type.charAt(0).toUpperCase() + type.slice(1);
  return { title: rest.join(":"), subtitle: label };
}

const NODE_ICON: Record<string, IconDefinition> = {
  company: faBuilding,
  platform: faLayerGroup,
  resource: faCube,
  group: faUsers,
};

// One level of the access tree. A node with a grant shows its role and, for an
// editor, a button to revoke it; a node that is only on the way down to a grant
// is dimmed.
function TreeBranch({
  nodes,
  canEdit,
  onRevoke,
}: {
  nodes: AccessTreeNode[];
  canEdit: boolean;
  onRevoke: (grant: AccessGrant) => void;
}) {
  return (
    <ul className="client-access-tree">
      {nodes.map((node) => (
        <li key={node.id}>
          <div
            className="client-api-node client-access-tree-row"
            data-granted={node.grants.length > 0}
          >
            <Icon icon={NODE_ICON[node.type] ?? faCircle} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", overflowWrap: "anywhere" }}>
                {node.label}
              </span>
              <span className="bfc-base-2" style={{ display: "block" }}>
                {node.type.charAt(0).toUpperCase() + node.type.slice(1)}
              </span>
            </div>
            {node.grants.map((grant) => {
              const role = node.type === "group" ? "member" : grant.relation;
              return (
                <span
                  key={`${grant.object}:${grant.relation}`}
                  style={{ display: "contents" }}
                >
                  <RoleBadge role={role} />
                  <Tooltip content="Revoke access">
                    <Button
                      variant="flat"
                      state="alert"
                      small
                      className="client-api-revoke"
                      aria-label={`Revoke ${role} on ${node.label}`}
                      disabled={!canEdit}
                      onClick={() => onRevoke(grant)}
                    >
                      <Icon icon={faTrash} />
                    </Button>
                  </Tooltip>
                </span>
              );
            })}
          </div>
          {node.children.length > 0 && (
            <TreeBranch
              nodes={node.children}
              canEdit={canEdit}
              onRevoke={onRevoke}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

// What this client can act on itself: its permissions in the FGA store, the same
// kind of permissions users have, plus the requests still waiting for approval.
export function ClientDataAccess({ client }: { client: ClientListItem }) {
  const canEdit = useCanEditClient(client);
  const { data: grants = [] } = useClientAccess(client.clientId);
  const { data: requests = [] } = useClientRequests(client.id);
  const revoke = useRevokeAccess(client.id);
  const cancel = useCancelRequest(client.id);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<{
    target: string;
    role: string;
  } | null>(null);

  const { data: companies = [] } = useCompanies();
  const tree = buildAccessTree(
    grants,
    (id) => companies.find((c) => c.id === id)?.displayName ?? undefined,
  );

  // The requests that are not granted yet; granted access is in the tree.
  const items: DataItem[] = requests
    .filter((r) => r.requestType === "data-access" && r.status !== "approved")
    .map((r) => {
      const isGroup = r.target.startsWith("group:");
      return {
        key: `req:${r.id}`,
        ...describeTarget(r.target),
        role: isGroup ? "member" : r.requestedRole || undefined,
        status: r.status,
        onCancel:
          r.status === "pending" ? () => cancel.mutate(r.id) : undefined,
      };
    });

  const error = revoke.error ?? cancel.error;

  return (
    <Section
      border
      radius
      background="base-3"
      className="client-api-access client-data-access"
    >
      <Section.Header>
        <Inline align="center">
          <Icon icon={faUserLock} />
          <Inline.Stretch>Data access</Inline.Stretch>
          <Tooltip content="Add data access">
            <Button
              variant="flat"
              small
              className="client-icon-button"
              aria-label="Add data access"
              disabled={!canEdit}
              onClick={() => {
                setNotice(null);
                setDrawerOpen(true);
              }}
            >
              <Icon icon={faPlus} />
            </Button>
          </Tooltip>
        </Inline>
      </Section.Header>
      <Section.Content className="client-api-access-body client-data-access-body">
        {notice && <Message state={notice.state}>{notice.text}</Message>}
        {error && (
          <Message state="alert" header="Could not update data access">
            {error instanceof Error ? error.message : "Something went wrong."}
          </Message>
        )}

        {tree.length === 0 && items.length === 0 && (
          <div className="client-api-node">
            <span className="bfc-base-2" style={{ fontStyle: "italic" }}>
              No data access added
            </span>
          </div>
        )}

        {/* The granted access as a tree down the object hierarchy. */}
        {tree.length > 0 && (
          <TreeBranch
            nodes={tree}
            canEdit={canEdit}
            onRevoke={(g) =>
              setPendingRemoval({ target: g.object, role: g.relation })
            }
          />
        )}

        {/* Requests still waiting for an answer, or refused, as rows. */}
        {items.length > 0 && (
          <Grid gap={8}>
            {items.map((item) => {
              const status = STATUS[item.status];
              return (
                <div key={item.key} className="client-api-node">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{ display: "block", overflowWrap: "anywhere" }}
                    >
                      {item.title}
                    </span>
                    <span className="bfc-base-2" style={{ display: "block" }}>
                      {item.subtitle}
                    </span>
                  </div>
                  {item.status !== "approved" && status && (
                    <Badge
                      pill
                      state={status.state}
                      className="client-api-status"
                    >
                      <Icon icon={status.icon} marginRight />
                      {status.label}
                    </Badge>
                  )}
                  {item.role && <RoleBadge role={item.role} />}
                  {item.onRevoke && (
                    <Tooltip content="Revoke access">
                      <Button
                        variant="flat"
                        state="alert"
                        small
                        className="client-api-revoke"
                        aria-label={`Revoke access to ${item.title}`}
                        disabled={!canEdit}
                        onClick={item.onRevoke}
                      >
                        <Icon icon={faTrash} />
                      </Button>
                    </Tooltip>
                  )}
                  {item.onCancel && (
                    <Tooltip content="Cancel request">
                      <Button
                        variant="flat"
                        state="alert"
                        small
                        className="client-api-revoke"
                        aria-label={`Cancel request for ${item.title}`}
                        disabled={!canEdit}
                        onClick={item.onCancel}
                      >
                        <Icon icon={faTrash} />
                      </Button>
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </Grid>
        )}

        {/* Pinned to the bottom right corner of the dotted body. */}
        <Link
          className="bf-button bf-button-flat client-data-docs"
          to="/docs/authorization/managing-access#broad-grants-to-services"
        >
          Go to documentation
          <Icon icon={faArrowRight} marginLeft />
        </Link>

        <AddPermissionDrawer
          clientId={client.id}
          isSpa={client.appType === "spa"}
          mode={drawerOpen ? "data-access" : null}
          siblingApiClientIds={[]}
          onClose={() => setDrawerOpen(false)}
          onResult={(n) => {
            setNotice(n);
            setDrawerOpen(false);
          }}
        />

        <Modal
          isOpen={pendingRemoval !== null}
          onRequestClose={() => setPendingRemoval(null)}
          header="Remove data access"
          width={480}
        >
          <Grid gap={16}>
            <p className="bf-p" style={{ margin: 0 }}>
              Remove access to <strong>{pendingRemoval?.target}</strong>? It
              might need approval again if you add it back.
            </p>
            <div
              style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
            >
              <Button variant="flat" onClick={() => setPendingRemoval(null)}>
                Cancel
              </Button>
              <Button
                variant="filled"
                state="alert"
                onClick={() => {
                  if (pendingRemoval) {
                    revoke.mutate(pendingRemoval, {
                      onSuccess: () =>
                        setNotice({
                          state: "success",
                          text: `Removed access to ${pendingRemoval.target}.`,
                        }),
                    });
                  }
                  setPendingRemoval(null);
                }}
              >
                Remove
              </Button>
            </div>
          </Grid>
        </Modal>
      </Section.Content>
    </Section>
  );
}
